import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { StatCard } from '../../components/StatCard';
import { Badge } from '../../components/Badge';
import {
  Users,
  Monitor,
  Activity,
  Moon,
  Clock,
  Sparkles,
  AlertTriangle,
  RefreshCw,
  FileText,
  Coffee,
  ChevronRight,
} from 'lucide-react';
import { ReportModal } from '../../components/ReportModal';
import { haptics } from '../../lib/haptics';
import { sounds } from '../../lib/sounds';

export function CustomerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const fetchData = async () => {
    try {
      const res = await api.admin.getDashboard();
      setData(res);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Auto-polling every 3.5s in background so dashboard is 100% live
    const interval = setInterval(() => {
      api.admin.getDashboard()
        .then((res) => setData(res))
        .catch(() => {});
    }, 3500);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    haptics.tap();
    sounds.playTapSound();
    setRefreshing(true);
    fetchData();
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <div className="h-8 w-64 skeleton rounded-2xl" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="h-28 skeleton rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  const kpi = data?.kpi || {};
  const subscription = data?.subscription;
  const recentEvents = data?.recentEvents || [];

  return (
    <div className="p-3 sm:p-6 lg:p-8 space-y-5 sm:space-y-8 max-w-7xl mx-auto min-w-0 w-full animate-ios-enter">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-white tracking-tight">
              {getGreeting()}, {user?.fullName?.split(' ')[0] || 'Admin'}
            </h1>
            <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 island-pulse-active">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span>Live Telemetry</span>
            </div>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
            {data?.organization?.name || user?.organization?.name || 'Workspace'} • Workstation Inactivity & Alerting Center
          </p>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            onClick={() => {
              haptics.selection();
              setIsReportModalOpen(true);
            }}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-md shadow-blue-500/20 ios-press transition"
          >
            <FileText className="w-3.5 h-3.5" />
            <span>Executive Dossier (PDF)</span>
          </button>

          <button
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-xl bg-white/80 dark:bg-zinc-900 hover:bg-white dark:hover:bg-zinc-800 text-zinc-800 dark:text-zinc-200 border border-black/[0.08] dark:border-white/10 ios-press shadow-sm"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Subscription Banner */}
      {subscription && (
        <div
          className={`p-4 sm:p-5 rounded-2xl ios-card flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
            subscription.status === 'EXPIRED'
              ? 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300'
              : 'border-black/[0.06] dark:border-white/[0.08]'
          }`}
        >
          <div className="flex items-center gap-3.5">
            <div className="p-2.5 rounded-2xl bg-pulse-blue/10 border border-pulse-blue/20 text-pulse-blue shadow-sm">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                <span>Subscription Plan: {subscription.duration.replace('_', ' ')}</span>
                <Badge status={subscription.status} />
              </div>
              <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5 font-medium">
                {subscription.isLifetime
                  ? 'Permanent lifetime enterprise tier — no expiration.'
                  : subscription.expiresAt
                  ? `Active until ${new Date(subscription.expiresAt).toLocaleDateString()} (${subscription.remainingDays} days remaining)`
                  : 'Subscription active'}
              </p>
            </div>
          </div>

          <div className="text-right">
            <span className="text-[11px] font-mono text-zinc-500 dark:text-zinc-400">
              Inactivity Limit: <strong className="text-zinc-900 dark:text-white">{data?.organization?.inactivityThresholdSeconds || 180}s</strong>
            </span>
          </div>
        </div>
      )}

      {/* Pending Break Approvals Banner */}
      {Number(kpi.pendingBreakApprovals || 0) > 0 && (
        <div className="p-4 sm:p-5 rounded-2xl bg-amber-500/10 dark:bg-amber-950/30 border-2 border-amber-500/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xl shadow-amber-500/10 animate-ios-enter">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-500 shrink-0 shadow-sm">
              <Coffee className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <div className="text-xs font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                <span>{kpi.pendingBreakApprovals} Break Approval Request{kpi.pendingBreakApprovals > 1 ? 's' : ''} Pending</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold font-mono bg-amber-500 text-white dark:text-black">
                  Action Required
                </span>
              </div>
              <p className="text-[11px] text-zinc-600 dark:text-zinc-400 mt-0.5">
                Workstation users are awaiting permission to start their scheduled break.
              </p>
            </div>
          </div>
          <Link
            to="/admin/live"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-amber-500 hover:bg-amber-400 text-black shadow-md shadow-amber-500/20 transition-all ios-press shrink-0"
          >
            <span>Review & Approve on Live Monitor</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* KPIs Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <StatCard
          title="Total Users"
          value={kpi.totalUsers ?? 0}
          subtitle="Registered staff"
          icon={<Users className="w-5 h-5" />}
        />
        <StatCard
          title="Online Devices"
          value={kpi.onlineDevices ?? 0}
          subtitle={`${kpi.totalDevices ?? 0} Total Workstations`}
          icon={<Monitor className="w-5 h-5" />}
        />
        <StatCard
          title="Active Workstations"
          value={kpi.activeDevices ?? 0}
          subtitle="User presence detected"
          icon={<Activity className="w-5 h-5 text-emerald-500" />}
        />
        <StatCard
          title="On Break"
          value={kpi.onBreakDevices ?? 0}
          subtitle="Alerts muted"
          icon={<Coffee className="w-5 h-5 text-blue-500" />}
        />
        <StatCard
          title="Inactive Alerts"
          value={kpi.inactiveDevices ?? 0}
          subtitle="Threshold exceeded"
          icon={<Moon className="w-5 h-5 text-amber-500" />}
        />
      </div>

      {/* Recent Inactivity Events */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-zinc-900 dark:text-white tracking-tight">Recent Inactivity Events</h2>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Triggered when workstation idle duration exceeded configured threshold
            </p>
          </div>
        </div>

        <div className="ios-card rounded-2xl overflow-hidden shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-black/[0.03] dark:bg-zinc-900/90 text-zinc-500 dark:text-zinc-400 uppercase tracking-wider font-bold border-b border-black/[0.06] dark:border-white/[0.08]">
                <tr>
                  <th className="py-3.5 px-4">User</th>
                  <th className="py-3.5 px-4">Device</th>
                  <th className="py-3.5 px-4">Inactivity Started</th>
                  <th className="py-3.5 px-4">Duration</th>
                  <th className="py-3.5 px-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/[0.04] dark:divide-white/[0.05] text-zinc-700 dark:text-zinc-300">
                {recentEvents.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-zinc-500 font-medium">
                      No inactivity events recorded yet today.
                    </td>
                  </tr>
                ) : (
                  recentEvents.map((evt: any) => {
                    const mins = Math.floor(evt.durationSeconds / 60);
                    const secs = evt.durationSeconds % 60;
                    const durationText = `${mins}m ${secs}s`;

                    return (
                      <tr key={evt.id} className="hover:bg-black/[0.02] dark:hover:bg-zinc-800/40 transition-colors">
                        <td className="py-3.5 px-4 font-bold text-zinc-900 dark:text-white">
                          <div>{evt.user?.fullName || 'Workstation User'}</div>
                          <div className="text-[11px] text-zinc-500">{evt.user?.email}</div>
                        </td>
                        <td className="py-3.5 px-4 font-mono text-zinc-700 dark:text-zinc-300">
                          {evt.device?.deviceName || evt.device?.hostname || 'Workstation'}
                        </td>
                        <td className="py-3.5 px-4 text-zinc-500 dark:text-zinc-400">
                          {new Date(evt.startedAt).toLocaleTimeString()}
                        </td>
                        <td className="py-3.5 px-4 font-mono font-bold text-amber-600 dark:text-amber-400">
                          {durationText}
                        </td>
                        <td className="py-3.5 px-4">
                          <Badge status={evt.status} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
      />
    </div>
  );
}
